import { useCallback, useContext, useRef, useState } from "react";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { declineOfficeRequest, getOfficeRequest, setOfficeRequestReason, setOfficeRequestUrgent } from "../../redux/slices/OfficeRequestSlice";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  Button,
  Checkbox,
  Comment,
  ConfirmationDialog,
  FormErrorMessage,
  FullScreenLoader,
  ProtectedComponent,
  RequestOverViewFileUpload,
  TextInput,
} from "../../components";
import { AuthPermission, UserState } from "../../services/auth.service";
import { billedToOptions, formatDate, formatOhipNumber } from "../../utils";
import {
  getOfficeRequestPatientMessages,
  addOfficeRequestMessage,
  getOfficeRequestInternalMessages,
  setOfficeRequestMessageRead,
  generatePatientLetter,
} from "../../redux/slices/RequestSlice";
import { RequestState, RequestType, RequestVerificationStateType } from "../../models/request.model";
import { ToastVariants } from "../../models";
import { MentionsInput, Mention } from "react-mentions";
import defaultStyle from "../../utils/defaultStyle";
import { User } from "@sentry/react";
import { getOfficeUsersWithPermission } from "../../redux/slices/OfficeSlice";
import { useNavigate } from "react-router-dom";
import classNames from "../../assets/css/mention.module.css";

import PatientCommunication from "./PatientCommunication";
import { isThirdPartyRequest } from "../../utils/request";
import { ServiceBilledTo } from "../../models/service.model";

const RequestOverviewTab = ({
  requestId,
  officeId,
  handleFromVisibility,
}: {
  requestId: string | undefined;
  officeId: string | undefined;
  handleFromVisibility: (val: RequestVerificationStateType["visibleForm"] | null) => void;
}) => {
  const divRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [readOnly, setReadOnly] = useState(false);
  const [getRequestData, setGetRequestData] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [internalPending, setInternalPending] = useState(false);
  const [externalPending, setExternalPending] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [staff, setStaff] = useState<{ display: string; id: string }[]>([]);
  const toast = useContext(ToastContext);
  const { requestData, loading } = useSelector((state: RootState) => state.officeRequest);
  const { userWithPermission } = useSelector((state: RootState) => state.office);
  const { currentUser } = useSelector((state: RootState) => state.currentUser);
  const { internalMessage, patientMessage } = useSelector((state: RootState) => state.request);
  const currentOffice = useSelector((state: RootState) => state.office.currentOffice);
  const permissions = currentUser?.permissions;
  const [commentInputs, setCommentInputs] = useState({ internal: "", patientCommunication: "" });
  const [patientLetterContent, setPatientLetterContent] = useState("");
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState<string>("");
  const [declineReasonOtherText, setDeclineReasonOtherText] = useState("");
  const [declineReasonError, setDeclineReasonError] = useState<string | null>(null);
  const [isDeclineLoading, setIsDeclineLoading] = useState(false);
  const isAuthorizedToEdit = Array.isArray(permissions) && permissions.includes(AuthPermission.Request);

  const declineReasonOptions = [
    {
      value: "Request is for an appointment",
      title: "Request is for an appointment",
      description: "Use when patient mistakenly raises an appointment-type request.",
    },
    {
      value: "Invalid or incomplete request",
      title: "Invalid or incomplete request",
      description: "Use when details provided are insufficient or incorrect.",
    },
    {
      value: "other",
      title: "Other (specify below)",
      description: "",
    },
  ];
  const isCompletedOrCancelled = requestData?.state === RequestState.Complete || requestData?.state === RequestState.Cancelled;
  const isCancelled = requestData?.state === RequestState.Cancelled;
  const isArchived = requestData?.archived;
  const showDeclineRequest = requestData?.state === RequestState.PendingVerification && requestData?.requestType === RequestType.Standard;
  const isThirdParty = isThirdPartyRequest(requestData?.requestType);
  const showRequestType = [RequestType.ThirdPartyOffice, RequestType.ServiceCanadaOffice, RequestType?.WSIBOffice];
  const showBilledTo = requestData?.requestType && showRequestType.includes(requestData?.requestType);

  const openForm = (formName: RequestVerificationStateType["visibleForm"]) => {
    handleFromVisibility(formName);
  };

  const onMentionClick = () => {
    toast?.openToast();
  };

  const handleScroll = async () => {
    if (requestData?.assignedTo ? requestData?.assignedTo?.userId === currentUser?.userId : true) {
      const patientUnReadMessage = patientMessage?.filter(f => f.unread === true);
      const internalUnReadMessage = internalMessage?.filter(f => f.unread === true);
      if (internalUnReadMessage?.length || patientUnReadMessage?.length) {
        const setReadPromises: any = [];
        patientUnReadMessage?.forEach(async e => {
          if (officeId && requestId && e.requestId === requestId) {
            const promise = dispatch(setOfficeRequestMessageRead({ officeId, requestId, messageId: e.requestMessageId, read: true }));
            setReadPromises.push(promise);
          }
        });
        internalUnReadMessage?.forEach(async e => {
          if (officeId && requestId && e.requestId === requestId) {
            const promise = dispatch(setOfficeRequestMessageRead({ officeId, requestId, messageId: e.requestMessageId, read: true }));
            setReadPromises.push(promise);
          }
        });

        await Promise.all(setReadPromises);

        if (officeId && requestId) {
          dispatch(getOfficeRequest({ officeId, requestId }));
          dispatch(getOfficeRequestPatientMessages({ officeId, requestId }));
          dispatch(getOfficeRequestInternalMessages({ officeId, requestId }));
        }
      }
    }
  };

  const handleUrgent = (checked: boolean) => {
    if (officeId && requestId)
      dispatch(setOfficeRequestUrgent({ officeId: officeId, requestId: requestId, urgent: checked })).then(action => {
        if (action.payload !== null) toast?.openToast("Status successfully changed.");
      });
  };

  const sendPatientCommentMessage = () => {
    if (commentInputs.patientCommunication.trim() !== "" && officeId && requestId && currentUser) {
      setExternalPending(true);
      dispatch(
        addOfficeRequestMessage({
          officeId: officeId,
          requestId: requestId,
          message: commentInputs.patientCommunication,
          internal: false,
          senderId: currentUser?.userId,
        })
      ).then(() => {
        setExternalPending(false);
        dispatch(getOfficeRequest({ officeId, requestId }));
        setCommentInputs(comments => {
          return { ...comments, patientCommunication: "" };
        });
        dispatch(getOfficeRequestPatientMessages({ officeId, requestId }));
      });
    }
  };

  const sendInternalCommentMessage = () => {
    if (commentInputs.internal.trim() !== "" && officeId && requestId && currentUser) {
      setInternalPending(true);
      dispatch(
        addOfficeRequestMessage({
          officeId: officeId,
          requestId: requestId,
          message: commentInputs.internal,
          internal: true,
          senderId: currentUser?.userId,
        })
      ).then(() => {
        setInternalPending(false);
        dispatch(getOfficeRequest({ officeId, requestId }));
        setCommentInputs(comments => {
          return { ...comments, internal: "" };
        });
        dispatch(getOfficeRequestInternalMessages({ officeId, requestId }));
      });
    }
  };

  const readAction = (messageId: string, read: boolean) => {
    if (requestId && officeId) {
      dispatch(setOfficeRequestMessageRead({ officeId, requestId, messageId, read })).then(_ => {
        dispatch(getOfficeRequestPatientMessages({ officeId, requestId }));
      });
    }
  };

  const handleGeneratePatientLetter = async () => {
    if (!officeId || !requestId) return;
    if (patientLetterContent.trim() === "") {
      toast?.openToast("Enter Message is required");
      return;
    }
    const response = await dispatch(generatePatientLetter({ officeId, requestId, message: patientLetterContent.trim() }));
    const ResponseData = response?.payload as any;
    setPatientLetterContent("");
    const cleanedMessage = ResponseData?.message?.replace(/:.*/, "").trim();
    if (ResponseData?.message) {
      toast?.openToast(cleanedMessage + ".");
    } else {
      dispatch(getOfficeRequest({ officeId, requestId }));
      toast?.openToast("Patient letter generated.");
    }
  };
  const init = async () => {
    if (requestId && officeId) {
      await dispatch(getOfficeRequest({ officeId, requestId }));
      setGetRequestData(true);
      await dispatch(getOfficeUsersWithPermission({ officeId, permission: AuthPermission.Request, page: 0, sort: "", size: 100 }));
      await dispatch(getOfficeRequestInternalMessages({ officeId, requestId }));
      await dispatch(getOfficeRequestPatientMessages({ officeId, requestId }));
      setInitialized(true);
    }
  };

  useEffect(() => {
    init();
  }, [officeId, requestId]);

  useEffect(() => {
    let newOptions = userWithPermission?.data?.content
      .filter(item => item.userState === "active")
      ?.map((user: User) => {
        return { display: `${user.firstName} ${user.lastName}`, id: user.userId };
      });
    newOptions = newOptions ? newOptions : [];
    setStaff(newOptions);
  }, [userWithPermission]);

  useEffect(() => {
    if (isCompletedOrCancelled || isArchived) {
      setReadOnly(true);
    } else setReadOnly(false);
  }, [requestData]);

  useEffect(() => {
    if (getRequestData && requestData?.requestId === undefined) {
      navigate("/offices");
    }
  }, [getRequestData, requestData]);

  const handleIntersect: IntersectionObserverCallback = useCallback(
    (entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Run your function when the div becomes visible
          handleScroll();
          // If you only want the function to run once, you can unobserve the element
          observer.unobserve(entry.target);
        }
      });
    },
    [initialized, requestData, currentUser]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersect, {
      root: null, // Use the viewport as the root
      rootMargin: "0px", // Add negative 100px margin to the bottom
      threshold: 0.1, // Trigger when 10% of the target is visible
    });

    if (divRef.current) {
      observer.observe(divRef.current);
    }

    return () => {
      if (divRef.current) {
        observer.disconnect();
      }
    };
  }, [handleIntersect]);

  useEffect(() => {
    localStorage.removeItem("additionalFee");
  }, []);

  const extractMentionText = (message: string) => {
    const match = message.match(/\[##.*?\|(.*?)##]/);
    return match ? match[1] : "";
  };
  const cleanMentions = (message: string) => {
    return message.replace(/\[##.*?\|(.*?)##]/g, "$1").trim();
  };
  const formatMentions = (message: string) => {
    return message.replace(/\[##.*?\|(.*?)##]/g, (_, mention) => {
      return `<span class="font-sm text-docsigna-purple-dark">${mention}</span>`;
    });
  };

  const declineReasonSelection = () => {
    return (
      <div className="px-5 font-manrope rounded-lg ">
        <div className="mb-2">Are you sure you want to decline this request? This action cannot be undone.</div>
        <div className="flex flex-col gap-5">
          {declineReasonOptions.map(option => (
            <div key={option.value} className="flex items-start gap-3">
              <input
                id={`decline-reason-${option.value}`}
                type="radio"
                checked={declineReason === option.value}
                onChange={() => {
                  setDeclineReason(option.value);
                  setDeclineReasonError(null);
                }}
                className="mt-1 w-4 h-4 cursor-pointer"
                style={{ accentColor: "#FF6B6B" }}
              />
              <div className="flex flex-col flex-1">
                <label htmlFor={`decline-reason-${option.value}`} className="text-base font-bold text-docsigna-blue-dark cursor-pointer">
                  {option.title}
                </label>
                <p className="text-sm italic text-docsigna-blue-dark mt-1 opacity-90">{option.description}</p>
              </div>
            </div>
          ))}
        </div>
        {declineReason === "other" && (
          <div className="mt-6">
            <TextInput
              onChangeFunc={e => {
                setDeclineReasonOtherText(e.target.value);
                if (declineReasonError) {
                  setDeclineReasonError(null);
                }
              }}
              value={declineReasonOtherText}
              label=""
              placeholder="Specify reason here"
              isTextArea
              extraInputClass="resize-none min-h-24 bg-white text-docsigna-blue-dark"
              isFocused={declineReason === "other"}
            />
            {declineReasonError && <FormErrorMessage text={declineReasonError} />}
          </div>
        )}
      </div>
    );
  };

  const handleDeclineRequest = async () => {
    if (!declineReason) {
      setDeclineReasonError("Please select a reason");
      return;
    }

    // Validate character limit for "other" decline reason
    if (declineReason === "other" && declineReasonOtherText.trim().length > 1000) {
      setDeclineReasonError("Character limit should be less than 1000 characters.");
      return;
    }

    if (declineReason === "other" && !declineReasonOtherText.trim()) {
      setDeclineReasonError("Please specify the reason");
      return;
    }

    setDeclineReasonError(null);
    setIsDeclineLoading(true);

    try {
      // Use title for predefined options, custom text for "other"
      const selectedOption = declineReasonOptions.find(opt => opt.value === declineReason);
      const reasonToSend = declineReason === "other" ? declineReasonOtherText.trim() : selectedOption?.title || declineReason;

      if (officeId && requestId) {
        const response = await dispatch(declineOfficeRequest({ officeId, requestId, reason: reasonToSend }));

        if (response.payload !== null) {
          await dispatch(getOfficeRequestPatientMessages({ officeId, requestId }));
          await dispatch(getOfficeRequest({ officeId, requestId }));
          await dispatch(
            setOfficeRequestReason({
              officeId: officeId,
              requestId: requestId,
              reason: declineReason === "other" ? declineReasonOtherText : declineReason,
            })
          );
          toast?.openToast("Request Declined");
          setShowDeclineModal(false);
          setDeclineReason("");
          setDeclineReasonOtherText("");
        } else {
          toast?.openToast("Something went wrong.", 2000, ToastVariants.Error);
        }
      }
    } catch (error) {
      toast?.openToast("Something went wrong.", 2000, ToastVariants.Error);
    } finally {
      setIsDeclineLoading(false);
    }
  };

  return (
    <div className="flex flex-wrap md:flex-nowrap">
      {/* new code start */}
      {loading && requestData?.patientVerified === undefined ? <FullScreenLoader /> : null}
      <div className="w-full  mb-6 md:mb-0">
        <div className="flex items-center mb-3">
          <p className="text-base font-semibold">Patient Details</p>
          {requestData?.patient?.userState === UserState.Invited && requestData?.requestType !== RequestType.DocnoteDebit ? (
            <Button
              onClick={() => null}
              text={"Pending Account Creation"}
              width="fit"
              className={`py-1.5 ml-2 !bg-docnote-green !border-docnote-green !text-black`}
            />
          ) : (
            <Button
              onClick={() => (isAuthorizedToEdit && !readOnly ? openForm("VerifyPatient") : null)}
              text={requestData?.patientVerified ? "Verified" : "Unverified"}
              width="fit"
              verified={requestData?.patientVerified}
              className={`py-1.5 ml-2`}
            />
          )}

          {showDeclineRequest ? (
            <span className="ml-auto">
              <Button onClick={() => setShowDeclineModal(true)} className="pointer py-1.5" text="Decline Request" width="fit" />
            </span>
          ) : null}
        </div>
        <div className="p-6 pt-2 w-full bg-docsigna-pink-light rounded-lg mb-6">
          {readOnly ? null : (
            <div className="flex justify-end">
              <ProtectedComponent requiredPermission={[AuthPermission.Request]}>
                <span className="text-docsigna-blue-light text-sm cursor-pointer font-medium" onClick={() => openForm("VerifyPatient")}>
                  Edit
                </span>
              </ProtectedComponent>
            </div>
          )}
          <p className="text-md mb-2">
            <span className="font-medium">Health Card Number:</span> {requestData?.ohipNumber ? formatOhipNumber(requestData?.ohipNumber) : "-"}
          </p>
          <p className="text-md mb-2">
            <span className="font-medium">Date of Birth:</span> {requestData?.dateOfBirth ? formatDate(requestData?.dateOfBirth, "PP") : "-"}
          </p>
          <p className="text-md mb-2">
            <span className="font-medium">Email Address:</span>{" "}
            {requestData?.email ? (
              readOnly ? (
                <>{requestData?.email}</>
              ) : (
                <a href={`mailto:${requestData?.email}`} className="text-docsigna-purple-dark font-medium">
                  {requestData?.email}
                </a>
              )
            ) : (
              "-"
            )}
          </p>
          <p className="text-md flex">
            <span className="font-medium">Phone #:</span> {requestData?.phone ? requestData?.phone : "-"}
            {showBilledTo ? null : (
              <div>
                {((isThirdParty && requestData?.patient?.userId) || !isThirdParty) && requestData?.phone && !readOnly ? (
                  <span className="text-docsigna-purple-dark ml-5 cursor-pointer font-medium" onClick={() => openForm("SendMessage")}>
                    Send Text
                  </span>
                ) : null}
              </div>
            )}
          </p>
        </div>

        <div className="flex justify-between items-center flex-wrap mb-3">
          <p className="text-base font-semibold w-full md:w-auto mb-2 md:mb-0">Request Details</p>
          {readOnly ? null : (
            <Checkbox
              label="Flag request as URGENT"
              checked={requestData?.urgent}
              onChange={e => handleUrgent(e.currentTarget.checked)}
              name="urgent_checkbox"
              id="urgent_checkbox"
            />
          )}
        </div>
        <div className="p-6 w-full border border-docsigna-pink rounded-lg mb-6">
          <p className="text-md mb-2">
            <span className="font-medium">Request#:</span> {requestData?.requestNumber}
          </p>
          <p className="text-md mb-2">
            <span className="font-medium">Date:</span> {formatDate(requestData?.createdDate ? requestData?.createdDate : "", "PP")}
          </p>
          <p className="text-md mb-2">
            <>
              <span className="font-medium">Service:</span> &nbsp;
              <span
                className={`text-md ${readOnly ? "" : "text-docsigna-blue-light font-medium cursor-pointer"}`}
                onClick={() => {
                  if (!requestData?.patientVerified) {
                    toast?.errorToast("Please verify the user first.");
                    return;
                  }
                  isAuthorizedToEdit && !readOnly ? openForm("AssignService") : null;
                }}
              >
                {requestData?.service ? requestData.service.name : "Unassigned"}
              </span>
            </>
          </p>
          {/* {requestData?.service?.description && (
            <div className="mb-2 text-md">
              <span className="font-medium">Service Description: </span>
              <span className="text-sm">{requestData?.service?.description ?? ""}</span>
            </div>
          )} */}
          {requestData && requestData.serviceNotes ? (
            <p className="text-md mb-2">
              <span className="font-medium">Service Notes:</span> {requestData?.serviceNotes ? requestData?.serviceNotes : ""}
            </p>
          ) : null}
          {requestData?.serviceBilledToOverride ? (
            <p className="text-md mb-2">
              <span className="font-medium">Billed To:</span>
              &nbsp;{" "}
              <span
                onClick={() => {
                  isAuthorizedToEdit && !readOnly && !isThirdParty ? openForm("AssignService") : null;
                }}
                className={`text-md ${isThirdParty || readOnly ? "" : "text-docsigna-blue-light font-medium cursor-pointer"}`}
              >
                {requestData?.serviceBilledToOverride ? billedToOptions[requestData?.serviceBilledToOverride] : requestData?.service?.serviceBilledTo}
              </span>
            </p>
          ) : null}
          <p className="text-md mb-2">
            <span className="font-medium">Assigned to:</span>
            &nbsp;{" "}
            <span
              onClick={() => {
                if (!requestData?.patientVerified) {
                  toast?.errorToast("Please verify the user first.");
                  return;
                }
                isAuthorizedToEdit && !readOnly ? openForm("AssignRequest") : null;
              }}
              className={`text-md ${readOnly ? "" : "text-docsigna-blue-light font-medium cursor-pointer"}`}
            >
              {requestData && requestData.assignedTo ? `${requestData.assignedTo.firstName} ${requestData.assignedTo.lastName}` : " Unassigned"}
            </span>
          </p>

          <p className="text-md mb-2">
            <span className="font-medium">Primary Care Provider:</span>
            &nbsp;
            <span
              onClick={() => {
                if (!requestData?.patientVerified) {
                  toast?.errorToast("Please verify the user first.");
                  return;
                }
                isAuthorizedToEdit && !readOnly ? openForm("AssignRequest") : null;
              }}
              className={`text-md ${readOnly ? "" : "text-docsigna-blue-light font-medium cursor-pointer"}`}
            >
              {" "}
              {requestData && requestData.primaryCareProvider
                ? `${requestData.primaryCareProvider.firstName} ${requestData.primaryCareProvider.lastName}`
                : " Unassigned"}
            </span>
          </p>
          {requestData?.requestType !== RequestType?.DocnoteDebit ? (
            <p className="text-md mb-2">
              <span className="font-medium"> Recipients: </span>
              &nbsp;{" "}
              <span className="text-md ">
                {requestData?.recipientName ? `Patient, ${requestData?.recipientName}  (${requestData?.recipientContact})` : readOnly ? "--" : null}{" "}
              </span>
              {readOnly ? null : (
                <ProtectedComponent requiredPermission={[AuthPermission.Request]}>
                  <span
                    className="cursor-pointer text-md text-docsigna-blue-light font-medium"
                    onClick={() => {
                      if (isAuthorizedToEdit) openForm("Recipients");
                    }}
                  >
                    &nbsp; Edit
                  </span>
                </ProtectedComponent>
              )}
            </p>
          ) : null}

          <p className="text-md mb-2">
            <span className="font-medium">Patient Request Detail Notes: </span>
            {readOnly ? null : (
              <ProtectedComponent requiredPermission={[AuthPermission.Request]}>
                <span
                  className="cursor-pointer text-md text-docsigna-blue-light font-medium"
                  onClick={() => {
                    if (isAuthorizedToEdit && requestData?.requestType) {
                      if ([RequestType.ThirdPartyOffice, RequestType.WSIBOffice, RequestType.ServiceCanadaOffice].includes(requestData?.requestType))
                        openForm("EditNotes");
                      else setShowConfirmDialog(true);
                    }
                  }}
                >
                  &nbsp; Edit
                </span>
              </ProtectedComponent>
            )}
          </p>
          <p className="text-sm">{requestData?.details ?? ""}</p>
        </div>
        {requestData?.primaryCareProvider &&
          requestData?.serviceBilledToOverride === ServiceBilledTo?.Patient &&
          requestData?.requestType === RequestType?.Standard && (
            <div className="mb-3">
              <TextInput
                onChangeFunc={e => setPatientLetterContent(e.currentTarget.value)}
                placeholder={"Patient letter Content"}
                value={patientLetterContent}
                errorMsg={"Enter Message is required"}
                isRequired={false}
                subLabel={""}
                label={"Patient letter Content"}
                key={`message-field`}
                isTextArea
                isError={false}
                extraInputClass="resize-none mb-4"
              />
              <div className="flex justify-end">
                <Button
                  text="Generate"
                  onClick={handleGeneratePatientLetter}
                  width="fit"
                  name="generate-patient-letter"
                  className=""
                  disabled={patientLetterContent.trim() === ""}
                />
              </div>
            </div>
          )}
        {requestData?.requestType !== RequestType.DocnoteDebit ? (
          <RequestOverViewFileUpload officeId={officeId} requestId={requestId} readOnly={readOnly} />
        ) : null}

        {/* Start of Comment section */}
        <div className="flex justify-between items-center mb-3" ref={divRef}>
          <p className="text-base font-semibold">Internal Communication</p>
        </div>
        <div
          className={`w-full mb-6 ${
            (internalMessage && internalMessage.length) || (!isCancelled && !isArchived) ? "p-6 border border-docsigna-pink rounded-lg" : ""
          }`}
        >
          {internalMessage && internalMessage.length ? (
            <div>
              {internalMessage?.map(cmt => (
                <Comment
                  onMentionClick={onMentionClick}
                  time={cmt.createdDate}
                  from={`${cmt.sender?.firstName} ${cmt.sender?.lastName}`}
                  // text={cmt.message}
                  text={cleanMentions(cmt.message)}
                  key={cmt.requestMessageId}
                  staff={staff}
                  comment={cmt}
                  checkMssage={extractMentionText(cmt.message)}
                  newVal={formatMentions(cmt.message)}
                />
              ))}
            </div>
          ) : isCancelled || isArchived ? (
            "-"
          ) : null}
          {!isCancelled && !isArchived ? (
            <div className="text-right">
              <MentionsInput
                style={defaultStyle}
                placeholder="Add a message to internal staff"
                value={commentInputs.internal}
                className="mentions"
                classNames={classNames}
                onChange={(e, newValue) => {
                  setCommentInputs({ ...commentInputs, internal: newValue });
                }}
              >
                <Mention
                  trigger={"@"}
                  data={staff}
                  className={classNames.mentions__mention}
                  displayTransform={(id, display) => {
                    return `@${display}`;
                  }}
                  appendSpaceOnAdd={true}
                  markup="[##__id__|@__display__##]"
                />
              </MentionsInput>
              <Button
                text="Send"
                disabled={internalPending}
                onClick={() => {
                  sendInternalCommentMessage();
                }}
                width="fit"
                name="send-internal-comment"
                className="mt-4 px-5"
              />
            </div>
          ) : null}
        </div>

        {!showBilledTo && (
          <PatientCommunication
            messages={patientMessage}
            onSubmit={sendPatientCommentMessage}
            isRequestArchived={isArchived}
            isRequestCancelled={isCancelled}
            onMentionClick={onMentionClick}
            currentOffice={currentOffice}
            currentUser={currentUser}
            readAction={readAction}
            isSubmitDisabled={externalPending}
            inputValue={commentInputs.patientCommunication}
            onChangeInput={e => setCommentInputs({ ...commentInputs, patientCommunication: e.currentTarget.value })}
            onBlurInput={e => setCommentInputs({ ...commentInputs, patientCommunication: e.currentTarget.value.trim() })}
          />
        )}

        <ConfirmationDialog
          open={showConfirmDialog}
          title={"Patient will see any changes made to the notes section.  Please confirm you would like to proceed."}
          description={""}
          successButtonText={"Confirm"}
          handleSuccess={() => {
            openForm("EditNotes");
            setShowConfirmDialog(false);
          }}
          failureButtonText={"Cancel"}
          handleFailure={() => {
            setShowConfirmDialog(false);
          }}
        />

        <ConfirmationDialog
          open={showDeclineModal}
          title={""}
          description={declineReasonSelection()}
          successButtonText={"Decline"}
          handleSuccess={handleDeclineRequest}
          failureButtonText={"Cancel"}
          handleFailure={() => {
            setShowDeclineModal(false);
            setDeclineReason("");
            setDeclineReasonOtherText("");
            setDeclineReasonError(null);
          }}
          disableSuccess={!declineReason || (declineReason === "other" && !declineReasonOtherText?.trim())}
          loading={isDeclineLoading}
        />

        {/* End of Comment section */}
      </div>
      {/* new code end */}
    </div>
  );
};

export default RequestOverviewTab;
